import { CallTaskChanges, CallTaskDetail } from '@/services/lifecycleAPI';

export const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export type CallTaskForm = {
  outcome: string;
  assignee: string;
  roles: string;
  days: string[];
  notes: string;
};

export const parseRoles = (text: string): string[] =>
  text
    .split(',')
    .map((role) => role.trim())
    .filter(Boolean);

// preferred_roles is a 255-character column, and the save joins the roles
// with ", ", so "a,b" typed is "a, b" saved. Check the saved length.
export const ROLES_MAX_LENGTH = 255;

export const rolesError = (text: string): string | null => {
  const saved = parseRoles(text).join(', ').length;
  return saved > ROLES_MAX_LENGTH
    ? `Preferred roles are too long: ${saved} characters once saved, the limit is ${ROLES_MAX_LENGTH}. Remove some roles.`
    : null;
};

const toList = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (typeof value === 'string') return parseRoles(value);
  return [];
};

export const formFromTask = (task: CallTaskDetail): CallTaskForm => ({
  outcome: task.outcome ?? '',
  assignee:
    task.assigned_to_user_id === null ? '' : String(task.assigned_to_user_id),
  roles: toList(task.preferred_roles).join(', '),
  days: toList(task.preferred_days),
  notes: task.notes ?? '',
});

const dayOrder = (day: string) => {
  const index = DAYS.indexOf(day);
  return index === -1 ? DAYS.length : index;
};

export const sortDays = (days: string[]): string[] =>
  [...days].sort((a, b) => dayOrder(a) - dayOrder(b));

const sameList = (a: string[], b: string[]) =>
  JSON.stringify(a) === JSON.stringify(b);

export const changedFields = (
  task: CallTaskDetail,
  form: CallTaskForm,
): CallTaskChanges => {
  const before = formFromTask(task);
  const changes: CallTaskChanges = {};
  if (form.notes !== before.notes) changes.notes = form.notes;
  if (form.assignee !== before.assignee) {
    changes.assigned_to_user_id =
      form.assignee === '' ? null : Number(form.assignee);
  }
  const roles = parseRoles(form.roles);
  if (!sameList(roles, parseRoles(before.roles)))
    changes.preferred_roles = roles.join(', ') || null;
  const days = sortDays(form.days);
  if (!sameList(days, sortDays(before.days)))
    changes.preferred_days = days.join(', ') || null;
  return changes;
};
